import React, { useState, useEffect } from "react";
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
  Checkbox,
  Dropdown,
  IDropdownOption,
} from "@fluentui/react";
import {
  IntegrationDto,
  listIntegrations,
} from "@/api/integrations";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";

export interface ConfigurationFilterOptions {
  statusFilter: "all" | "active" | "inactive";
  integrationId: number | undefined;
  customerId: number | undefined;
  scopeType: number | undefined;
}

export const defaultConfigFilters: ConfigurationFilterOptions = {
  statusFilter: "all",
  integrationId: undefined,
  customerId: undefined,
  scopeType: undefined,
};

export interface ConfigurationFilterPanelProps {
  isOpen: boolean;
  onDismiss: () => void;
  selectedFilters: ConfigurationFilterOptions;
  onFiltersChange: (filters: ConfigurationFilterOptions) => void;
  onApplyFilters: (filters: ConfigurationFilterOptions) => void;
  onClearFilters: () => void;
}

const ConfigurationFilterPanel: React.FC<ConfigurationFilterPanelProps> = ({
  isOpen,
  onDismiss,
  selectedFilters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}) => {
  const [localFilters, setLocalFilters] =
    useState<ConfigurationFilterOptions>(selectedFilters);
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);

  const handleApply = () => {
    onFiltersChange(localFilters);
    onApplyFilters(localFilters);
    onDismiss();
  };

  const handleClear = () => {
    const clearedFilters: ConfigurationFilterOptions = {
      ...defaultConfigFilters,
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onClearFilters();
    onApplyFilters(clearedFilters);
    onDismiss();
  };

  const handleStatusChange = (option: "all" | "active" | "inactive") => {
    setLocalFilters((prev) => ({ ...prev, statusFilter: option }));
  };

  const hasActiveFilters = () => {
    return (
      localFilters.statusFilter !== "all" ||
      localFilters.integrationId !== undefined ||
      localFilters.customerId !== undefined ||
      localFilters.scopeType !== undefined
    );
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (selectedFilters.statusFilter !== "all") count++;
    if (selectedFilters.integrationId !== undefined) count++;
    if (selectedFilters.customerId !== undefined) count++;
    if (selectedFilters.scopeType !== undefined) count++;
    return count;
  };

  useEffect(() => {
    if (isOpen && integrations.length === 0) {
      setLoadingIntegrations(true);
      listIntegrations(undefined, 1000)
        .then((data) => setIntegrations(data?.result || []))
        .catch(() => setIntegrations([]))
        .finally(() => setLoadingIntegrations(false));
    }
  }, [isOpen, integrations.length]);

  useEffect(() => {
    setLocalFilters(selectedFilters);
  }, [selectedFilters]);

  const integrationOptions: IDropdownOption[] = [
    { key: "", text: "All Integrations" },
    ...integrations.map((i) => ({
      key: i.id,
      text: `${i.code} - ${i.name}`,
    })),
  ];

  return (
    <Panel
      headerText="Filter Configurations"
      isOpen={isOpen}
      onDismiss={onDismiss}
      type={PanelType.smallFixedFar}
      closeButtonAriaLabel="Close filter panel"
      isLightDismiss={true}
    >
      <div style={{ padding: "20px 0" }}>
        {/* Status Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Status</h4>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "8px" }}
          >
            <Checkbox
              label="All"
              checked={localFilters.statusFilter === "all"}
              onChange={() => handleStatusChange("all")}
            />
            <Checkbox
              label="Active"
              checked={localFilters.statusFilter === "active"}
              onChange={() => handleStatusChange("active")}
            />
            <Checkbox
              label="Inactive"
              checked={localFilters.statusFilter === "inactive"}
              onChange={() => handleStatusChange("inactive")}
            />
          </div>
        </div>

        {/* Integration Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>
            Integration
          </h4>
          <Dropdown
            placeholder={
              loadingIntegrations
                ? "Loading integrations..."
                : "Select an integration"
            }
            options={integrationOptions}
            selectedKey={localFilters.integrationId ?? ""}
            onChange={(_, option) =>
              setLocalFilters((prev) => ({
                ...prev,
                integrationId: option?.key
                  ? (option.key as number)
                  : undefined,
              }))
            }
            disabled={loadingIntegrations}
            styles={{ root: { width: "100%" } }}
          />
        </div>

        {/* Scope Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Scope</h4>
          <Dropdown
            placeholder="Select a scope"
            options={[
              { key: "", text: "All Scopes" },
              { key: 1, text: "Global" },
              { key: 2, text: "Customer" },
              { key: 3, text: "User" },
            ]}
            selectedKey={localFilters.scopeType ?? ""}
            onChange={(_, option) =>
              setLocalFilters((prev) => ({
                ...prev,
                scopeType: option?.key ? (option.key as number) : undefined,
              }))
            }
            styles={{ root: { width: "100%" } }}
          />
        </div>

        {/* Customer Filter */}
        <div style={{ marginBottom: "20px" }}>
          <h4 style={{ marginBottom: "10px", color: "#323130" }}>Customer</h4>
          <CustomerComboBox
            label=""
            selectedCustomerId={localFilters.customerId}
            onChange={(id) =>
              setLocalFilters((prev) => ({ ...prev, customerId: id }))
            }
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "8px",
            marginTop: "30px",
            paddingTop: "20px",
            borderTop: "1px solid #edebe9",
          }}
        >
          <PrimaryButton text="Apply Filters" onClick={handleApply} />
          <DefaultButton
            text="Clear All"
            onClick={handleClear}
            disabled={!hasActiveFilters()}
          />
        </div>

        {getActiveFiltersCount() > 0 && (
          <div
            style={{
              marginTop: "15px",
              fontSize: "12px",
              color: "#605e5c",
            }}
          >
            Active filters: {getActiveFiltersCount()} filter(s) applied
          </div>
        )}
      </div>
    </Panel>
  );
};

export default ConfigurationFilterPanel;
